/**
 * Account-pair Status picker — labeled dropdown (Active / Paused).
 *
 * The pair-wide kill switch. Active means trading can flow on this
 * pair (manual or AI-driven, depending on the AI Mode setting);
 * Paused means NO trading at all — the manual-trade routes reject
 * with `pair_paused`, the HedgePair heartbeat skips Haiku, and the
 * sleep/wake controls keep working but won't trade if the operator
 * wakes the legs.
 *
 * Orthogonal to the AI Mode picker (which scopes Haiku's reach when
 * trading IS happening) and to Sleep/Wake (which controls the Brave
 * window, not the trading gate). Flipping Status preserves both —
 * resume restores everything you had configured.
 *
 * Backed by `account_pairs.ai_paused` — column name is legacy from
 * when the semantic was AI-only; the gate now blocks every trade
 * type. Renaming the column would mean a migration churn for a name
 * change with no behavioural impact, so the column stays.
 */

import { useCallback, useState, type ReactNode } from "react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { extractApiErrorMessage } from "@/lib/api";
import { setAccountPairAiPaused } from "@/lib/api/account-pairs";
import { cn } from "@/lib/utils";
import type { PairMode } from "@pfh/shared";

const MODE_LABEL: Record<PairMode, string> = {
  manual: "Manual",
  semi_auto: "Semi",
  full_auto: "Auto",
};

interface PairAiPauseButtonProps {
  /** account_pairs.slug — the public id used by /ai-paused. */
  accountPairSlug: string;
  /** Current paused state, sourced from account_pairs.ai_paused. */
  paused: boolean;
  /** Configured AI mode. Surfaced in the Active option's hint so the
   *  operator knows which AI mode resumes alongside trading. */
  aiMode: PairMode;
  /** Whether the parent pair is operationally live. Disables when not. */
  disabled?: boolean;
  size?: "sm" | "default";
  className?: string;
  /** Parent calls this after a successful flip so it can refetch and
   *  let the optimistic state settle. */
  onChanged?: () => void;
}

type Slot = "active" | "paused";

export function PairAiPauseButton({
  accountPairSlug,
  paused,
  aiMode,
  disabled,
  size = "default",
  className,
  onChanged,
}: PairAiPauseButtonProps): ReactNode {
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const effective = optimistic ?? paused;
  const slot: Slot = effective ? "paused" : "active";

  const flip = useCallback(
    async (next: boolean) => {
      if (disabled || busy || next === effective) return;
      setOptimistic(next);
      setBusy(true);
      try {
        await setAccountPairAiPaused(accountPairSlug, next);
        onChanged?.();
      } catch (e) {
        setOptimistic(null);
        toast.error(extractApiErrorMessage(e));
      } finally {
        setBusy(false);
        queueMicrotask(() => setOptimistic(null));
      }
    },
    [accountPairSlug, busy, disabled, effective, onChanged],
  );

  return (
    <Select
      value={slot}
      onValueChange={(v) => void flip(v === "paused")}
      disabled={disabled || busy}
    >
      <SelectTrigger
        size={size === "sm" ? "sm" : "default"}
        aria-label="Pair status — active or paused"
        className={cn(
          "gap-2",
          size === "sm" ? "text-[11px]" : "text-xs",
          effective
            ? "border-destructive/50 text-destructive hover:border-destructive/70"
            : "border-emerald-500/40 text-emerald-500 hover:border-emerald-500/60",
          (disabled || busy) && "opacity-60",
          className,
        )}
      >
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Pair Status
        </span>
        <SelectValue>
          <span
            className={cn(
              "font-medium",
              effective ? "text-destructive" : "text-emerald-500",
            )}
          >
            {effective ? "Paused" : "Active"}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent
        align="end"
        alignItemWithTrigger={false}
        className="min-w-72"
      >
        <SelectItem value="active">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-emerald-500">Active</span>
            <span className="text-[10px] text-muted-foreground">
              Trading allowed. AI mode: {MODE_LABEL[aiMode]}.
            </span>
          </div>
        </SelectItem>
        <SelectItem value="paused">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-destructive">Paused</span>
            <span className="text-[10px] text-muted-foreground">
              No trading — manual or AI. Mode + sleep state preserved.
            </span>
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
